// snake_case wire shapes -> camelCase view types for the /reports/* endpoints.
// One pure mapper per report. View types stay camelCase downstream.
import type { IssueAgingRow, IssuesReportPage, OfficerPerfPage, SurveysReportPage } from './types'

interface RawOfficerPerfRow {
  officer_id: string
  officer_name: string
  mandal: string
  assigned: number
  completed: number
  missed: number
  completion_pct: number | null
}

export interface RawOfficerPerfResponse {
  rows: RawOfficerPerfRow[]
  meta?: { pagination?: { total_count?: number } } | null
}

export function mapOfficerPerformance(raw: RawOfficerPerfResponse): OfficerPerfPage {
  return {
    rows: (raw.rows ?? []).map((r) => ({
      officerId: r.officer_id,
      officerName: r.officer_name,
      mandal: r.mandal,
      assigned: r.assigned,
      completed: r.completed,
      missed: r.missed,
      completionPct: r.completion_pct,
    })),
    totalCount: raw.meta?.pagination?.total_count ?? null,
  }
}

interface RawSurveyRow {
  assignment_id?: string // backend's row key (handoff §7)
  id?: string // legacy key — kept as a fallback until every env ships the rename
  fo_name: string | null
  mandal: string
  survey_name: string
  survey_ref: string
  scheduled_date: string | null
  status: string
}

export interface RawSurveysResponse {
  rows: RawSurveyRow[]
  // meta carries pagination (total_count) + per-status counts for the full filtered set (handoff §2–§3).
  meta?: {
    pagination?: { total_count?: number }
    counts?: { pending?: number; missed?: number; completed?: number; total?: number }
  } | null
}

function mapSurveyCounts(c: NonNullable<NonNullable<RawSurveysResponse['meta']>['counts']> = {}): SurveysReportPage['counts'] {
  const pending = c.pending ?? 0
  const missed = c.missed ?? 0
  const completed = c.completed ?? 0
  return { pending, missed, completed, total: c.total ?? pending + missed + completed }
}

export function mapSurveysReport(raw: RawSurveysResponse): SurveysReportPage {
  return {
    rows: (raw.rows ?? []).map((r) => ({
      id: r.assignment_id ?? r.id ?? '',
      foName: r.fo_name,
      mandal: r.mandal,
      surveyName: r.survey_name,
      surveyRef: r.survey_ref,
      scheduledDate: r.scheduled_date,
      status: r.status,
    })),
    counts: mapSurveyCounts(raw.meta?.counts),
    totalCount: raw.meta?.pagination?.total_count ?? null,
  }
}

interface RawIssueRow {
  issue_id: string
  issue_ref: string
  title: string
  severity: string
  status: string
  fo_name: string | null
  hod_name: string | null
  mandal: string | null
  site_id?: string | null
  site_name: string | null
  entity_name: string | null
  category?: string | null
  raised_at?: string // backend's row key (handoff §7)
  created_at?: string // legacy key — kept as a fallback until every env ships the rename
  days_active?: number | null
  resolution_date?: string | null
  days_to_resolve?: number | null
}

export interface RawIssuesResponse {
  rows: RawIssueRow[]
  // meta carries pagination (total_count) + group counts + (parity) severity/category aggregates.
  meta?: {
    pagination?: { total_count?: number }
    counts?: {
      active?: number
      resolved?: number
      pending?: number
      by_severity?: { critical?: number; non_critical?: number }
      by_category?: { category: string; count: number }[]
    }
  } | null
}

function mapIssueRow(r: RawIssueRow): IssueAgingRow {
  return {
    issueId: r.issue_id,
    issueRef: r.issue_ref,
    title: r.title,
    severity: r.severity,
    status: r.status,
    foName: r.fo_name,
    hodName: r.hod_name,
    mandal: r.mandal,
    siteId: r.site_id ?? null,
    siteName: r.site_name,
    entityName: r.entity_name,
    category: r.category ?? null,
    createdAt: r.raised_at ?? r.created_at ?? '',
    daysActive: r.days_active ?? null,
    resolutionDate: r.resolution_date ?? null,
    daysToResolve: r.days_to_resolve ?? null,
  }
}

type RawIssueCounts = NonNullable<NonNullable<RawIssuesResponse['meta']>['counts']>

/** Severity tally → null when absent; each missing bucket defaults to 0. Kept separate so the
 *  main mapper stays under the complexity limit. */
function mapSeverityCounts(bs: RawIssueCounts['by_severity']): IssuesReportPage['severityCounts'] {
  return bs ? { critical: bs.critical ?? 0, non_critical: bs.non_critical ?? 0 } : null
}

function mapTopCategories(list: RawIssueCounts['by_category']): IssuesReportPage['topCategories'] {
  return (list ?? []).map((x) => ({ category: x.category, count: x.count }))
}

export function mapIssuesReport(raw: RawIssuesResponse): IssuesReportPage {
  const c = raw.meta?.counts ?? {}
  return {
    rows: (raw.rows ?? []).map(mapIssueRow),
    meta: { active: c.active ?? 0, resolved: c.resolved ?? 0, pending: c.pending ?? 0 },
    totalCount: raw.meta?.pagination?.total_count ?? null,
    severityCounts: mapSeverityCounts(c.by_severity),
    topCategories: mapTopCategories(c.by_category),
  }
}
